import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileWarning, Shield, AlertTriangle, Search, Filter, RefreshCw, ShieldBan, ShieldCheck, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getFileThreats, fileAction, explainRisk } from '../services/api';
import { toast } from 'sonner';

const threatClassColors = {
  critical: 'bg-red-500/10 text-red-500 border-red-500/20',
  high: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  medium: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  low: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  safe: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
};

const statusColors = {
  quarantined: 'bg-red-500/10 text-red-500',
  allowed: 'bg-emerald-500/10 text-emerald-500',
  released: 'bg-blue-500/10 text-blue-500',
};

export default function FileThreats() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);
  const [aiExplanation, setAiExplanation] = useState('');
  const [loadingExplanation, setLoadingExplanation] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getFileThreats();
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch file threats:', error);
      toast.error('Failed to load file threats');
    } finally {
      setLoading(false);
    }
  };

  const handleFileAction = async (fileId, action) => {
    try {
      await fileAction(fileId, action);
      toast.success(`File ${action} successful`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} file`);
    }
  };

  const handleGetExplanation = async (file) => {
    setSelectedFile(file);
    setLoadingExplanation(true);
    setAiExplanation('');
    try {
      const response = await explainRisk('file_threat', {
        file_name: file.file_name,
        classification: file.classification,
        risk_score: file.risk_score,
        behaviors: file.behaviors,
      });
      setAiExplanation(response.data.explanation);
    } catch (error) {
      setAiExplanation('Unable to generate explanation at this time.');
    } finally {
      setLoadingExplanation(false);
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.file_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterClass === 'all' || event.classification === filterClass;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: events.length,
    quarantined: events.filter((e) => e.status === 'quarantined').length,
    critical: events.filter((e) => e.classification === 'critical').length,
    high: events.filter((e) => e.classification === 'high').length,
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6" data-testid="file-threats-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl text-white">File Threats</h1>
        <p className="text-slate-400 mt-1">Monitor files scanned by the sandbox and endpoint agents</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <FileWarning className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-500">Total Scanned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <ShieldBan className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.quarantined}</p>
                <p className="text-xs text-slate-500">Quarantined</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.critical}</p>
                <p className="text-xs text-slate-500">Critical</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.high}</p>
                <p className="text-xs text-slate-500">High Risk</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <CardTitle className="text-lg font-heading text-white">File Scan Events</CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <Select value={filterClass} onValueChange={setFilterClass}>
                <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="safe">Safe</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchData} className="border-slate-700">
                <RefreshCw className="w-4 h-4 text-slate-400" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">File Name</TableHead>
                <TableHead className="text-slate-400">Size</TableHead>
                <TableHead className="text-slate-400">Risk Score</TableHead>
                <TableHead className="text-slate-400">Classification</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.map((event, index) => (
                <motion.tr
                  key={event.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className="border-slate-800 hover:bg-slate-800/50"
                >
                  <TableCell>
                    <div>
                      <p className="font-mono text-sm text-white truncate max-w-[200px]">{event.file_name}</p>
                      <p className="text-xs text-slate-500 font-mono">{event.file_hash?.slice(0, 12)}...</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-400">{formatFileSize(event.file_size)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            event.risk_score >= 75 ? 'bg-red-500' :
                            event.risk_score >= 50 ? 'bg-orange-500' :
                            event.risk_score >= 25 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${event.risk_score}%` }}
                        />
                      </div>
                      <span className="text-sm text-slate-400">{event.risk_score}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${threatClassColors[event.classification]} border`}>
                      {event.classification}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[event.status]}>
                      {event.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleGetExplanation(event)}
                        className="text-blue-500 hover:text-blue-400 hover:bg-blue-500/10"
                        data-testid={`explain-${event.id}`}
                      >
                        <Info className="w-4 h-4" />
                      </Button>
                      {event.status === 'quarantined' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFileAction(event.id, 'release')}
                          className="text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
                        >
                          <ShieldCheck className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFileAction(event.id, 'quarantine')}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <ShieldBan className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </motion.tr>
              ))}
              {filteredEvents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No file events found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* AI Explanation Dialog */}
      <Dialog open={!!selectedFile} onOpenChange={() => setSelectedFile(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-500" />
              AI Threat Analysis
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedFile && (
              <div className="p-3 rounded-lg bg-slate-800">
                <p className="font-mono text-sm text-white">{selectedFile.file_name}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={`${threatClassColors[selectedFile.classification]} border`}>
                    {selectedFile.classification}
                  </Badge>
                  <span className="text-sm text-slate-400">Risk: {selectedFile.risk_score}</span>
                </div>
                {selectedFile.behaviors?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-slate-500 mb-1">Detected Behaviors:</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedFile.behaviors.map((b, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-slate-700 text-slate-400">
                          {b.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <p className="text-sm text-slate-300 leading-relaxed">
                {loadingExplanation ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Analyzing threat...
                  </span>
                ) : (
                  aiExplanation || 'Click a file to get AI analysis.'
                )}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
